import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { formatApiError } from "@/contexts/AuthContext";
import { MagnifyingGlass, Plus, ArrowsClockwise, CheckCircle, Warning, X, ArrowSquareOut } from "@phosphor-icons/react";

const STATUS_COLOR = {
  ok: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-700",
};

// Slugify a free-form string the same way the backend does — lowercase, non-
// alphanumerics collapsed to a single '-'. Used only for local matching, so
// small differences (accents, punctuation) still line up with admin keys.
function norm(s) {
  return (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/**
 * Given the scraped breadcrumbs and the admin's Category tree, pick the
 * closest (category, subcategory) pair. Never invents anything — only
 * matches against `categories` the admin has already created.
 *
 * Strategy:
 *   1. Try each subcategory: full-slug match against any crumb (exact,
 *      substring either direction). First hit wins; picks its parent
 *      category too.
 *   2. Fallback: match a top-level category label/key against any crumb.
 *   3. Otherwise return empty strings so the admin sees "— select —".
 *
 * Returns {category, subcategory, matchedOn} — matchedOn is a short
 * human string for the UI hint ("Chains" or null).
 */
function suggestFromBreadcrumbs(crumbs, categories) {
  const empty = { category: "", subcategory: "", matchedOn: null };
  if (!Array.isArray(crumbs) || !crumbs.length || !Array.isArray(categories) || !categories.length) {
    return empty;
  }
  const normCrumbs = crumbs.map(norm).filter(Boolean);
  if (!normCrumbs.length) return empty;
  const overlap = (a, b) => a === b || a.includes(b) || b.includes(a);

  // Sub-category first — the more specific hit
  for (const cat of categories) {
    for (const sub of cat.subcategories || []) {
      const nsub = norm(sub);
      if (!nsub) continue;
      const crumb = normCrumbs.find((nc) => overlap(nc, nsub));
      if (crumb) {
        return { category: cat.key, subcategory: sub, matchedOn: `“${crumb.replace(/-/g, " ")}” → ${cat.label} → ${sub}` };
      }
    }
  }
  // Category fallback
  for (const cat of categories) {
    const nkey = norm(cat.key);
    const nlbl = norm(cat.label);
    const crumb = normCrumbs.find((nc) => overlap(nc, nkey) || (nlbl && overlap(nc, nlbl)));
    if (crumb) {
      return { category: cat.key, subcategory: "", matchedOn: `“${crumb.replace(/-/g, " ")}” → ${cat.label}` };
    }
  }
  return empty;
}

// Poll a scrape job until it terminates. Returns the final job doc on success,
// throws on failure or wall-clock timeout. Wall-clock cap is 90s per user
// spec — after that the backend job may still complete, but we tell the user
// to check back / retry.
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 90_000;

async function pollScrapeJob(jobId, onTick) {
  const startedAt = Date.now();
  // Give the background task a beat to start before the first poll — smooths
  // the "queued → running" transition and avoids a redundant first request.
  await new Promise((r) => setTimeout(r, 600));
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (Date.now() - startedAt > POLL_MAX_MS) {
      const err = new Error(`Scrape did not finish within ${POLL_MAX_MS / 1000}s. It may still complete in the background — refresh the imported products list shortly.`);
      err.timeout = true;
      throw err;
    }
    const { data } = await api.get(`/admin/import/status/${jobId}`);
    onTick?.(data);
    if (data.status === "success") return data;
    if (data.status === "failed") {
      const err = new Error(data.error || "Scrape failed with an unknown error.");
      err.jobStatus = data;
      throw err;
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
}

export default function AdminImport() {
  const [supported, setSupported] = useState([]);
  const [url, setUrl] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [previewStatus, setPreviewStatus] = useState(""); // human status text
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [brand, setBrand] = useState("");
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState({});
  const [sanitizing, setSanitizing] = useState(false);
  const [autoMatched, setAutoMatched] = useState(null);
  const [catsError, setCatsError] = useState(null);
  const [catsLoading, setCatsLoading] = useState(true);
  const activeJobRef = useRef(null);

  // Same data source as Admin → Categories and Admin → Products.
  //
  // Uses async/await + AbortController so a hung request (production
  // symptom: "Loading…" forever with a green preview beside it) can never
  // lock the UI: after 12s we abort and surface the failure. Also logs to
  // the browser console for diagnostics.
  const loadCategories = async () => {
    setCatsError(null);
    setCatsLoading(true);
    const controller = new AbortController();
    const watchdog = setTimeout(() => controller.abort(new Error("client watchdog: request took >12s")), 12000);
    try {
      const r = await api.get("/admin/categories", { signal: controller.signal });
      if (!Array.isArray(r.data)) throw new Error("Unexpected response shape");
      setCategories(r.data);
      // eslint-disable-next-line no-console
      console.info(`[AdminImport] loaded ${r.data.length} categories`);
    } catch (err) {
      const detail = err?.response?.status
        ? `HTTP ${err.response.status} — ${formatApiError(err.response?.data?.detail) || err.message}`
        : (err?.message || "Network error");
      // eslint-disable-next-line no-console
      console.error("[AdminImport] loadCategories failed:", err, detail);
      setCatsError(detail);
      setCategories([]);
    } finally {
      clearTimeout(watchdog);
      setCatsLoading(false);
    }
  };

  // Same data source as Admin → Categories and Admin → Products
  const currentCat = useMemo(
    () => categories.find((c) => c.key === category),
    [categories, category],
  );
  const availableSubs = currentCat?.subcategories || [];

  const load = () => {
    setLoading(true);
    api.get("/admin/import/products").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  const sanitizeAll = async () => {
    if (!window.confirm("Re-run the HTML sanitizer on every URL-imported product? This cleans stored descriptions in place — no scraping.")) return;
    setSanitizing(true);
    try {
      const { data } = await api.post("/admin/import/sanitize-all");
      toast.success(`Sanitized ${data.updated}/${data.scanned} products (${data.unchanged} already clean)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sanitize failed");
    } finally { setSanitizing(false); }
  };

  useEffect(() => {
    api.get("/admin/import/supported").then((r) => setSupported(r.data.domains));
    loadCategories();
    load();
  }, []);

  // Dependent dropdown — clear the subcategory whenever the category changes
  useEffect(() => {
    setSubcategory("");
  }, [category]);

  const humanStatus = (job) => {
    if (!job) return "";
    if (job.status === "queued") return "Queued — waiting for a scraper worker…";
    if (job.status === "running") return "Scraping supplier page (rendering with Chromium)…";
    if (job.status === "success") return "Done.";
    if (job.status === "failed") return `Failed: ${job.error || "unknown error"}`;
    return job.status;
  };

  const doPreview = async (e) => {
    e?.preventDefault();
    if (!url.trim()) { toast.error("Paste a product URL first"); return; }
    setPreview(null);
    setAutoMatched(null);
    setPreviewing(true);
    setPreviewStatus("Submitting…");
    try {
      const { data: enq } = await api.post("/admin/import/preview", { url: url.trim() });
      activeJobRef.current = enq.job_id;
      setPreviewStatus("Queued — scraping supplier page…");
      const final = await pollScrapeJob(enq.job_id, (tick) => setPreviewStatus(humanStatus(tick)));
      setPreview(final.result);
      // Auto-suggest from the source page's breadcrumb — only matches against
      // categories the admin has already created; leaves selection empty when
      // no confident match exists.
      const s = suggestFromBreadcrumbs(final.result?.breadcrumbs, categories);
      if (s.category) {
        setCategory(s.category);
        setSubcategory(s.subcategory);
        setAutoMatched(s.matchedOn);
      }
      toast.success("Preview loaded — review then import");
    } catch (err) {
      const msg = err.timeout ? err.message
        : (err.jobStatus?.error || formatApiError(err.response?.data?.detail) || err.message || "Preview failed");
      toast.error(msg);
    } finally {
      setPreviewing(false);
      setPreviewStatus("");
      activeJobRef.current = null;
    }
  };

  const doImport = async () => {
    if (!preview) return;
    if (!category) {
      toast.error("Please select a Category before importing");
      return;
    }
    setSaving(true);
    setSaveStatus("Submitting…");
    try {
      const { data: enq } = await api.post("/admin/import/create", {
        url: url.trim(), category, subcategory, brand,
      });
      activeJobRef.current = enq.job_id;
      setSaveStatus("Queued — creating product…");
      const final = await pollScrapeJob(enq.job_id, (tick) => setSaveStatus(humanStatus(tick)));
      if (final.result?.duplicate) {
        toast.success(`Already existed — reused: ${final.result.name}`);
      } else {
        toast.success(`Imported: ${final.result?.name || "product"}`);
      }
      setPreview(null); setUrl(""); setBrand("");
      load();
    } catch (err) {
      const msg = err.timeout ? err.message
        : (err.jobStatus?.error || formatApiError(err.response?.data?.detail) || err.message || "Import failed");
      toast.error(msg);
    } finally {
      setSaving(false);
      setSaveStatus("");
      activeJobRef.current = null;
    }
  };

  const doResync = async (id) => {
    setResyncing((r) => ({ ...r, [id]: true }));
    try {
      await api.post(`/admin/import/${id}/resync`);
      toast.success("Re-synced");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Re-sync failed");
    } finally { setResyncing((r) => ({ ...r, [id]: false })); }
  };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-import">
      <div className="mb-6">
        <div className="overline text-[color:var(--vt-orange)] mb-2">/ URL IMPORT</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Product URL Importer</h1>
        <p className="text-sm text-neutral-500 mt-2">
          Paste any product URL from supported sites — we'll extract title, images, description, price and apply your 30% markup automatically. Prices and stock refresh daily at 04:00 UTC.
        </p>
      </div>

      {/* Supported domains ribbon */}
      <div className="mb-6 flex flex-wrap gap-2">
        {supported.map((d) => (
          <span key={d} className="text-[10px] uppercase tracking-widest font-bold border border-neutral-300 px-2 py-1 bg-white">
            {d}
          </span>
        ))}
      </div>

      {/* Import form */}
      <div className="bg-white border border-neutral-200 p-6 mb-8" data-testid="import-form">
        <form onSubmit={doPreview} className="grid md:grid-cols-[1fr_auto] gap-3 mb-4">
          <input
            required type="url" className="vt-input"
            placeholder="https://www.bike24.de/p/shimano-xt-cassette-cs-m8100-12-fach-..."
            value={url} onChange={(e) => setUrl(e.target.value)}
            data-testid="import-url-input"
          />
          <button disabled={previewing} className="vt-btn-primary" data-testid="import-preview-btn">
            <MagnifyingGlass size={16} /> {previewing ? "Fetching…" : "Preview"}
          </button>
        </form>

        {previewing && previewStatus && (
          <div className="mb-4 text-xs text-neutral-600 flex items-center gap-2" data-testid="preview-status">
            <ArrowsClockwise size={12} className="animate-spin" /> {previewStatus}
          </div>
        )}

        {preview && (
          <div className="border-t border-neutral-200 pt-6" data-testid="import-preview">
            <div className="grid md:grid-cols-[240px_1fr] gap-6">
              <div className="aspect-square bg-neutral-100 overflow-hidden">
                {preview.images?.[0]
                  ? <img src={preview.images[0]} alt={preview.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">No image</div>}
              </div>
              <div>
                <div className="overline text-neutral-500 mb-1">{preview.source_domain} · SKU: {preview.sku || "—"}</div>
                <h3 className="font-display font-bold text-xl mb-3">{preview.name}</h3>
                <p className="text-sm text-neutral-700 mb-4 line-clamp-3">{preview.description}</p>

                <div className="grid grid-cols-3 gap-3 mb-4 text-xs">
                  <div className="p-3 border border-neutral-200 bg-neutral-50">
                    <div className="overline text-neutral-500">Supplier</div>
                    <div className="font-display font-bold text-lg">€{preview.supplier_price?.toFixed(2)}</div>
                  </div>
                  <div className="p-3 border border-[color:var(--vt-orange)] bg-yellow-50">
                    <div className="overline text-neutral-600">+30% Retail</div>
                    <div className="font-display font-black text-lg text-[color:var(--vt-orange-hover)]">€{preview.retail_price?.toFixed(2)}</div>
                  </div>
                  <div className="p-3 border border-neutral-200 bg-neutral-50">
                    <div className="overline text-neutral-500">Stock</div>
                    <div className="font-display font-bold text-lg">{preview.in_stock ? "✓ In" : "× Out"}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                  <div>
                    <label className="overline text-neutral-600 block mb-1">
                      Category*
                      {catsLoading && <span className="ml-2 text-[10px] text-neutral-400 normal-case">loading…</span>}
                    </label>
                    <select className="vt-input" value={category} onChange={(e) => { setCategory(e.target.value); setAutoMatched(null); }} data-testid="import-category">
                      <option value="">{categories.length === 0 && catsLoading ? "Loading…" : "— select —"}</option>
                      {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                    {catsError ? (
                      <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 flex-wrap" data-testid="cats-error">
                        <Warning size={12} weight="fill" />
                        <span>Couldn't load categories ({catsError}).</span>
                        <button type="button" onClick={loadCategories} className="underline font-semibold" data-testid="cats-retry">Retry</button>
                      </p>
                    ) : !catsLoading && categories.length === 0 ? (
                      <p className="text-[11px] text-red-600 mt-1">No categories yet. Create some in Admin → Categories.</p>
                    ) : null}
                  </div>
                  <div>
                    <label className="overline text-neutral-600 block mb-1">
                      Subcategory <span className="text-neutral-400 normal-case">(optional)</span>
                    </label>
                    <select className="vt-input" value={subcategory}
                      onChange={(e) => { setSubcategory(e.target.value); setAutoMatched(null); }}
                      disabled={!category || availableSubs.length === 0}
                      data-testid="import-subcategory">
                      <option value="">—</option>
                      {availableSubs.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {category && availableSubs.length === 0 && (
                      <p className="text-[11px] text-neutral-500 mt-1">No subcategories defined for this category yet.</p>
                    )}
                  </div>
                  <div>
                    <label className="overline text-neutral-600 block mb-1">Brand</label>
                    <input className="vt-input" placeholder="Shimano / SRAM / Bosch…" value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="import-brand" />
                  </div>
                </div>
                {autoMatched && (
                  <div className="mb-3 text-[11px] text-[color:var(--vt-orange-hover)] flex items-center gap-1.5" data-testid="auto-match-hint">
                    <CheckCircle size={12} weight="fill" />
                    <span>Auto-selected from source breadcrumb: <strong>{autoMatched}</strong>. You can override before importing.</span>
                  </div>
                )}
                {preview?.breadcrumbs?.length > 0 && !autoMatched && (
                  <div className="mb-3 text-[11px] text-neutral-500" data-testid="breadcrumb-nomatch">
                    Source breadcrumb: <span className="font-mono">{preview.breadcrumbs.join(" › ")}</span> · no confident match in your Categories tree, please pick manually.
                  </div>
                )}

                {(preview?.variants?.length > 0) && (
                  <div className="mb-4 border border-[color:var(--vt-orange)] bg-yellow-50 p-3" data-testid="preview-variants">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="overline text-[color:var(--vt-orange-hover)]">Detected variants</div>
                        <div className="text-[11px] text-neutral-700">
                          {preview.variants.length} row{preview.variants.length === 1 ? "" : "s"} · axis: <strong>{(preview.variant_options || []).join(", ")}</strong> · retail auto-computed +30% on save · edit any field afterwards in Admin → Products → Edit.
                        </div>
                      </div>
                    </div>
                    <table className="w-full text-[11px]">
                      <thead className="text-neutral-500">
                        <tr>
                          {(preview.variant_options || []).map((a) => <th key={a} className="text-left py-1 pr-2">{a}</th>)}
                          <th className="text-left py-1 pr-2">SKU</th>
                          <th className="text-right py-1 pr-2">Supplier</th>
                          <th className="text-right py-1 pr-2">Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.variants.map((v, i) => (
                          <tr key={i} className="border-t border-yellow-200" data-testid={`preview-variant-${i}`}>
                            {(preview.variant_options || []).map((a) => <td key={a} className="py-1 pr-2 font-mono">{v.options?.[a]}</td>)}
                            <td className="py-1 pr-2 font-mono">{v.sku || "—"}</td>
                            <td className="py-1 pr-2 text-right">€{Number(v.supplier_price || 0).toFixed(2)}</td>
                            <td className="py-1 pr-2 text-right">{Number(v.stock) > 0 ? "✓" : "×"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {preview.images?.length > 1 && (
                  <div className="mb-4">
                    <div className="overline text-neutral-500 mb-2">All {preview.images.length} images will be imported</div>
                    <div className="flex gap-2 flex-wrap">
                      {preview.images.slice(0, 6).map((img, i) => (
                        <img key={i} src={img} alt="" className="w-16 h-16 object-cover border border-neutral-200" />
                      ))}
                    </div>
                  </div>
                )}

                {saving && saveStatus && (
                  <div className="mb-3 text-xs text-neutral-600 flex items-center gap-2" data-testid="save-status">
                    <ArrowsClockwise size={12} className="animate-spin" /> {saveStatus}
                  </div>
                )}

                <div className="flex gap-3">
                  <button onClick={doImport} disabled={saving || !category} className="vt-btn-orange" data-testid="import-confirm-btn" title={!category ? "Select a Category first" : ""}>
                    <Plus size={16} /> {saving ? "Importing…" : "Import Product"}
                  </button>
                  <button onClick={() => setPreview(null)} className="vt-btn-outline" data-testid="import-cancel-btn"><X size={16} /> Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Imported list */}
      <div>
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display font-bold text-2xl tracking-tight">Imported products ({items.length})</h2>
          <div className="flex items-center gap-3">
            <button onClick={sanitizeAll} disabled={sanitizing || items.length === 0}
              className="text-[10px] uppercase tracking-widest font-bold border border-neutral-300 hover:border-black px-3 py-1.5 transition-colors disabled:opacity-40"
              title="Re-run the HTML sanitizer on every imported product's description in place"
              data-testid="sanitize-all-btn">
              {sanitizing ? "Sanitizing…" : "Clean Descriptions"}
            </button>
            <div className="text-xs text-neutral-500">Daily re-sync at 04:00 UTC · click <ArrowsClockwise size={12} className="inline" /> to refresh now</div>
          </div>
        </div>
        {loading ? <div className="p-12 text-center text-neutral-500">Loading…</div> :
        items.length === 0 ? <div className="p-12 text-center text-neutral-500 border border-neutral-200 bg-white">No URL-imported products yet. Paste a URL above to get started.</div> :
        <div className="bg-white border border-neutral-200" data-testid="import-list">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left p-3 overline text-neutral-600">Product</th>
                <th className="text-left p-3 overline text-neutral-600">Source</th>
                <th className="text-right p-3 overline text-neutral-600">Supplier</th>
                <th className="text-right p-3 overline text-neutral-600">Retail (+30%)</th>
                <th className="text-center p-3 overline text-neutral-600">Stock</th>
                <th className="text-left p-3 overline text-neutral-600">Last Sync</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`import-row-${p.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-12 h-12 object-cover border border-neutral-200" />}
                      <div>
                        <div className="font-bold">{p.name}</div>
                        <div className="text-[10px] text-neutral-500 uppercase">{p.category} · {p.brand}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <a href={p.source_url} target="_blank" rel="noreferrer" className="text-xs link-underline flex items-center gap-1">{p.source_domain} <ArrowSquareOut size={11} /></a>
                  </td>
                  <td className="p-3 text-right text-xs text-neutral-500">€{p.supplier_price?.toFixed(2)}</td>
                  <td className="p-3 text-right font-bold text-[color:var(--vt-orange-hover)]">€{p.price?.toFixed(2)}</td>
                  <td className="p-3 text-center">{p.stock > 0 ? <span className="text-green-700 font-bold">{p.stock}</span> : <span className="text-red-700">0</span>}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${STATUS_COLOR[p.source_sync_status] || "bg-neutral-100 text-neutral-600"}`}>
                      {p.source_sync_status === "ok" ? <CheckCircle size={10} className="inline mr-1" /> : <Warning size={10} className="inline mr-1" />}
                      {p.source_sync_status || "pending"}
                    </span>
                    <div className="text-[10px] text-neutral-500 mt-1">{p.source_last_synced_at?.slice(0, 16).replace("T", " ")}</div>
                    {p.source_last_error && <div className="text-[10px] text-red-600 mt-1 max-w-[220px] truncate" title={p.source_last_error}>{p.source_last_error}</div>}
                  </td>
                  <td className="p-3">
                    <button onClick={() => doResync(p.id)} disabled={resyncing[p.id]}
                      className="p-2 border border-neutral-300 hover:border-black transition-colors" title="Re-sync now"
                      data-testid={`resync-${p.id}`}>
                      <ArrowsClockwise size={14} className={resyncing[p.id] ? "animate-spin" : ""} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </div>
    </div>
  );
}
